// Root application component with route-driven layout

/**
 * @typedef {import('../../types/jsdoc-types.js').User} User
 */

import { css, html } from 'lit';
import { api, fetchConfig, setCDNConfig } from '../../lib/api.js';
import { authState, initAuth, login, logout } from '../../lib/auth.js';
import { BaseComponent } from '../../lib/base-component.js';
import { musicManager } from '../../lib/music-manager.js';
import { router } from '../../lib/router.js';
import { platformState } from '../../state/platform-state.js';

// Import layout components
import '../layout/nav-bar.js';
import '../layout/app-footer.js';
import '../layout/title-screen.js';
import '../layout/top-bar.js';
import '../shared/game-invitation-banner.js';
import '../shared/friend-request-banner.js';
import '../shared/app-viewport.js';
import '../shared/terms-of-service.js';
import '../shared/privacy-policy.js';
import '../shared/full-viewport.js';
import '../layout/mobile-nav-bar.js';
import '../layout/mobile-sign-in.js';
import '../layout/mobile-play-tab.js';

// Import view components
import '../views/account-view.js';
import '../views/games-view.js';
import '../views/adventures-view.js';
import '../views/play-view.js';
import '../views/start-adventure-view.js';
import '../views/characters-view.js';
import '../views/choose-character-mode-view.js';
import '../views/quick-create-view.js';
import '../views/create-character-view.js';
import '../views/character-detail-view.js';
import '../views/friends-accept-view.js';
import '../views/friends-view.js';
import '../views/notifications-view.js';
import '../views/game-view.js';
import '../views/game-spectator-view.js';
import '../views/access-denied-view.js';
import '../views/playtest-notes-view.js';
import '../views/join-game-view.js';

// Import staff views
import '../staff/staff-view.js';
import '../staff/staff-adventure-form-view.js';
import '../staff/staff-map-editor-view.js';
import '../staff/staff-equipment-form-view.js';
import '../staff/staff-class-editor-view.js';
import '../staff/staff-background-editor-view.js';
import '../staff/staff-feat-editor-view.js';
import '../staff/staff-species-editor-view.js';
import '../staff/staff-spell-editor-view.js';
import '../staff/staff-weapon-mastery-editor-view.js';
import '../staff/staff-monster-editor-view.js';
import '../staff/staff-game-events-view.js';

const MOBILE_COMING_SOON = {
  create: {
    label: 'Create',
    back: '/',
    description:
      "You've played our adventures, now make your own! Create will allow you to create adventures, monsters, spells, and entire campaigns.",
  },
  campaign: {
    label: 'Campaign',
    back: '/play',
    description:
      'Play through a series of connected adventures in the lands of ReplaceMe as your party seeks fortune and glory.',
  },
  survival: {
    label: 'Dungeon Survival',
    back: '/play',
    description:
      'Dungeon Survival pits you against other players as well as monsters and traps in a large, randomly-created dungeon to see who is the last PC standing.',
  },
};

/**
 * Root application component
 * Handles authentication state and route-driven layout rendering
 */
class RpgishApp extends BaseComponent {
  static properties = {
    loading: { type: Boolean, state: true },
    error: { type: String, state: true },
    user: { type: Object, state: true },
    currentRoute: { type: String, state: true },
    routeParams: { type: Object, state: true },
    desktopDownloadUrl: { type: String, state: true },
    d3AssetsBaseURL: { type: String, state: true },
    _mobileHeadingOverride: { type: String, state: true },
  };

  static styles = css`
    :host {
      display: block;
      width: 100%;
      height: 100vh;
    }

    .app-container {
      display: flex;
      width: 100%;
      height: 100vh;
    }

    .loading-container,
    .error-container {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100vh;
    }

    .main-layout {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100vh;
    }

    .content-area {
      display: flex;
      flex: 1;
      overflow: hidden;
    }

    .main-content {
      display: flex;
      flex-direction: column;
      flex: 1;
      overflow: hidden;
    }

    main {
      flex: 1;
      overflow: auto;
      position: relative;
      padding-bottom: 1.5rem;
    }

    .mobile-layout {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100dvh;
      background: #f8f9fa;
      color: #212529;
    }

    .mobile-content {
      flex: 1;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }

    .mobile-content.padded {
      padding: 1rem;
    }

    .mobile-page-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1rem;
      background: #ffffff;
      border-bottom: 1px solid #dee2e6;
    }

    .mobile-back-btn {
      background: none;
      border: none;
      color: #0d6efd;
      font-size: 1.2rem;
      padding: 0.25rem;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
    }

    .mobile-page-title {
      font-size: 1.1rem;
      font-weight: 600;
      color: #212529;
    }

    .mobile-coming-soon {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: 2rem 1.5rem;
      position: relative;
      background: center center / cover no-repeat;
    }

    .mobile-coming-soon::before {
      content: '';
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      pointer-events: none;
    }

    .mobile-coming-soon > * {
      position: relative;
      z-index: 1;
    }

    .coming-soon-title {
      font-family: 'MedievalSharp', Georgia, serif;
      font-size: 2.5rem;
      font-weight: 700;
      color: #ffffff;
      margin: 0 0 1rem 0;
      letter-spacing: 0.12em;
      text-shadow:
        0 0 8px rgba(0, 0, 0, 1),
        0 0 20px rgba(0, 0, 0, 0.9),
        0 0 40px rgba(0, 0, 0, 0.7),
        0 2px 4px rgba(0, 0, 0, 1);
    }

    .coming-soon-badge {
      display: inline-block;
      font-size: 1rem;
      letter-spacing: 0.15em;
      text-transform: uppercase;
      color: rgba(255, 255, 255, 0.9);
      border: 1px solid rgba(255, 255, 255, 0.5);
      border-radius: 4px;
      padding: 0.4rem 1rem;
      margin-bottom: 1.5rem;
    }

    .coming-soon-description {
      color: rgba(255, 255, 255, 0.85);
      font-size: 1.05rem;
      line-height: 1.6;
      max-width: 24rem;
      text-shadow:
        0 0 6px rgba(0, 0, 0, 1),
        0 0 16px rgba(0, 0, 0, 0.8);
    }
  `;

  constructor() {
    super();
    this.loading = true;
    /** @type {string|null} */
    this.error = null;
    /** @type {User|null} */
    this.user = null;
    this.currentRoute = '/';
    /** @type {{[key: string]: string}} */
    this.routeParams = {};
    this._isElectron = !!window.rpgishElectron?.isElectron;

    // Bind methods
    this._handleLogin = this._handleLogin.bind(this);
    this._handleLogout = this._handleLogout.bind(this);
    this._handleRouteChange = this._handleRouteChange.bind(this);
    this._handleAuthChange = this._handleAuthChange.bind(this);
  }

  async connectedCallback() {
    await super.connectedCallback();

    // Subscribe to router changes
    this._unsubscribeRouter = router.subscribe(this._handleRouteChange);

    // Subscribe to auth state changes
    this._unsubscribeAuth = authState.subscribe(this._handleAuthChange);

    // Subscribe to platform state changes
    this._unsubscribePlatform = platformState.subscribe(() => this.requestUpdate());

    // Listen for heading updates from child views
    this._handleHeadingUpdate = (e) => {
      this._mobileHeadingOverride = e.detail;
    };
    this.addEventListener('heading-update', this._handleHeadingUpdate);

    // Capacitor App plugin: pause/resume background music with app state.
    import('@capacitor/app')
      .then(({ App }) => {
        App.addListener('appStateChange', (state) => {
          if (!state.isActive) {
            musicManager.pauseAll();
          } else {
            musicManager.resumeIfPlaying();
          }
        });
      })
      .catch(() => {});

    // Initialize auth
    this._bootstrap();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribeRouter?.();
    this._unsubscribeAuth?.();
    this._unsubscribePlatform?.();
    this.removeEventListener('heading-update', this._handleHeadingUpdate);
  }

  async _bootstrap() {
    try {
      // Fetch d3-auth config from backend
      const config = await fetchConfig();
      this.desktopDownloadUrl = config.desktopDownloadUrl || '';
      this.d3AssetsBaseURL = config.d3AssetsBaseURL || '';

      // Configure CDN for d3-assets image URLs
      if (config.d3AssetsCDNURL && config.d3AssetsCDNID) {
        setCDNConfig(config.d3AssetsCDNURL, config.d3AssetsCDNID);
      }

      // OAuth URLs vary by platform. Native apps (Capacitor) and Electron
      // run in WebViews whose origin (capacitor://, https://localhost, app://)
      // is not a reachable HTTPS URL, so the redirect_uri and logout
      // returnTo must be real URLs claimed by the native app's Universal/App
      // Links so the OS hands control back to the app.
      const { Capacitor } = await import('@capacitor/core');
      let loginRedirectUri;
      let logoutReturnTo;
      if (Capacitor.isNativePlatform()) {
        const appOrigin = process.env.PUBLIC_APP_ORIGIN;
        if (!appOrigin) throw new Error('PUBLIC_APP_ORIGIN required for native builds');
        loginRedirectUri = `${appOrigin}/auth/callback`;
        logoutReturnTo = `${appOrigin}/logged-out`;
      } else if (window.rpgishElectron?.appOrigin) {
        loginRedirectUri = `${window.rpgishElectron.appOrigin}/auth/callback`;
        logoutReturnTo = window.rpgishElectron.appOrigin;
      }
      // else: plain web — kit defaults to window.location.origin

      // Initialize d3-auth via kit
      await initAuth({ ...config, appName: 'rpgish', loginRedirectUri, logoutReturnTo });

      // Merge RPGish backend data with d3-auth user data
      if (authState.isAuthenticated) {
        const meResponse = await api.fetch('/api/me');
        if (meResponse.status === 403) {
          // User is authenticated but doesn't have access to RPGish — the
          // API client already navigated to /access-denied.
          this.loading = false;
          return;
        }
        if (!meResponse.ok) {
          throw new Error(`Failed to load user: ${meResponse.status} ${meResponse.statusText}`);
        }
        const meData = await meResponse.json();
        // Merge: /api/me provides RPGish data, d3-auth provides identity (picture, name)
        authState.user = { ...meData, picture: authState.user?.picture || meData.picture };
        authState.notify();
      }

      // Update state from auth
      this.user = authState.user;
      this.loading = false;

      // Initialize current route (sets router.meta, router.params, notifies subscribers)
      router.handleRoute();
    } catch (err) {
      console.error('Failed to bootstrap application:', err);
      this.error = err instanceof Error ? err.message : String(err);
      this.loading = false;
    }
  }

  _handleRouteChange() {
    this.currentRoute = router.getPath();
    this.routeParams = router.params;
    this._mobileHeadingOverride = null;
    this._updateMusic();
    this.requestUpdate();
  }

  _updateMusic() {
    musicManager.switchTrack(router.meta?.music || 'title');
  }

  _handleAuthChange() {
    this.user = authState.user;
    this.requestUpdate();
  }

  async _handleLogin() {
    try {
      await login();
      // On Capacitor with in-app browser, login() resolves after token exchange.
      // Fetch RPGish user data to merge with d3-auth identity.
      if (authState.isAuthenticated) {
        const meResponse = await api.fetch('/api/me');
        if (meResponse.ok) {
          const meData = await meResponse.json();
          authState.user = { ...meData, picture: authState.user?.picture || meData.picture };
          authState.notify();
        }
        // Always land on /play after signing in, regardless of where login was triggered
        router.navigate('/play');
      }
    } catch (err) {
      if (err.message !== 'Login cancelled') {
        console.error('Login failed:', err);
      }
    }
  }

  _handleLogout() {
    logout();
    this.user = null;
  }

  /**
   * Create a view element dynamically from route metadata
   * @param {string} viewTag - Custom element tag name
   * @param {{[key: string]: string}} [attrsMap] - Map of element attribute names to route param names
   * @returns {import('lit').TemplateResult}
   */
  _createViewTemplate(viewTag, attrsMap) {
    const el = document.createElement(viewTag);
    if (attrsMap) {
      for (const [attr, paramName] of Object.entries(attrsMap)) {
        const value = this.routeParams[paramName];
        if (value) el.setAttribute(attr, value);
      }
    }
    return html`${el}`;
  }

  /**
   * Render the top bar (hidden in Electron)
   */
  _renderTopBar() {
    if (this._isElectron) return '';
    return html`<top-bar .user="${this.user}" @logout="${this._handleLogout}"></top-bar>`;
  }

  /**
   * Title layout: full-screen title screen with optional modal overlay
   * @param {object} meta - Route metadata
   */
  _renderTitleLayout(meta) {
    const modal = meta.modal;
    return html`
      <div class="main-layout">
        ${this._renderTopBar()}
        <game-invitation-banner></game-invitation-banner>
        <friend-request-banner></friend-request-banner>
        <div class="content-area">
          <div class="main-content">
            <title-screen
              .user="${this.user}"
              .desktopDownloadUrl="${this.desktopDownloadUrl}"
              menu-state="${meta.titleMenu || 'main'}"
              @login="${this._handleLogin}"
              ?hide-menu="${!!modal}"
            ></title-screen>
            ${
              modal
                ? html`
                  <app-viewport
                    heading="${modal.heading}"
                    ?no-padding="${modal.noPadding}"
                    back-href="${modal.backHref || ''}"
                    back-label="${modal.backLabel || ''}"
                    @close="${() => router.navigate('/')}"
                  >
                    ${this._createViewTemplate(modal.view, modal.attrs)}
                  </app-viewport>
                `
                : ''
            }
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Fullscreen layout: top-bar + full-viewport for game views
   * @param {object} meta - Route metadata
   */
  _renderFullscreenLayout(meta) {
    return html`
      <div class="main-layout">
        ${this._renderTopBar()}
        <full-viewport>
          ${this._createViewTemplate(meta.view, meta.attrs)}
        </full-viewport>
      </div>
    `;
  }

  /**
   * Staff layout: top-bar + staff-view with tabs
   * @param {object} meta - Route metadata
   */
  _renderStaffLayout(meta) {
    return html`
      <div class="main-layout">
        ${this._renderTopBar()}
        <div class="content-area">
          <div class="main-content">
            <main class="container-fluid p-4">
              <staff-view active-tab="${meta.staffTab}"></staff-view>
            </main>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Staff editor layout: top-bar + full-page editor (no tabs)
   * @param {object} meta - Route metadata
   */
  _renderStaffEditorLayout(meta) {
    return html`
      <div class="main-layout">
        ${this._renderTopBar()}
        <div class="content-area">
          <div class="main-content">
            <main class="container-fluid p-4">
              ${this._createViewTemplate(meta.view, meta.attrs)}
            </main>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Public layout: no auth, no top-bar
   * @param {object} meta - Route metadata
   */
  _renderPublicLayout(meta) {
    return html`
      <div class="app-container">
        ${this._createViewTemplate(meta.view, meta.attrs)}
      </div>
    `;
  }

  /**
   * Mobile layout: bottom nav bar + full-page content (no title screen, no top bar)
   * @param {object} meta - Route metadata
   */
  _renderMobileLayout(meta) {
    const modal = meta.modal;

    // Fullscreen layout (game) — no nav bar, game takes full screen
    if (meta.layout === 'fullscreen') {
      return html`
        <div class="mobile-layout">
          <full-viewport>
            ${this._createViewTemplate(meta.view, meta.attrs)}
          </full-viewport>
        </div>
      `;
    }

    // Determine content based on route
    let content;
    if (modal) {
      // Views that manage their own padding/background
      const selfPaddedViews = ['play-view', 'characters-view'];
      const needsPadding = !selfPaddedViews.includes(modal.view);
      // Modal routes render as full-page views with a back header
      content = html`
        <div class="mobile-page-header">
          <button class="mobile-back-btn" @click="${() => router.navigate(modal.backHref || '/play')}">
            <i class="bi bi-chevron-left"></i>
          </button>
          <span class="mobile-page-title">${this._mobileHeadingOverride || modal.heading}</span>
        </div>
        <div class="mobile-content ${needsPadding ? 'padded' : ''}">
          ${this._createViewTemplate(modal.view, modal.attrs)}
        </div>
      `;
    } else if (meta.titleMenu && meta.titleMenu !== 'play') {
      // Coming-soon title menu states (campaign, survival, create)
      const comingSoon = MOBILE_COMING_SOON[meta.titleMenu];
      if (comingSoon) {
        content = html`
          <div class="mobile-page-header">
            <button class="mobile-back-btn" @click="${() => router.navigate(comingSoon.back)}">
              <i class="bi bi-chevron-left"></i>
            </button>
            <span class="mobile-page-title">${comingSoon.label}</span>
          </div>
          <div
            class="mobile-content mobile-coming-soon"
            style="background-image: url('${process.env.PUBLIC_CDN_BASE_URL}/title-screen/adventurers-at-the-barrow')"
          >
            <h2 class="coming-soon-title">${comingSoon.label}</h2>
            <div class="coming-soon-badge">Coming Soon</div>
            <p class="coming-soon-description">${comingSoon.description}</p>
          </div>
        `;
      } else {
        content = html`<div class="mobile-content"><mobile-play-tab></mobile-play-tab></div>`;
      }
    } else {
      // Play tab (default landing)
      content = html`<div class="mobile-content"><mobile-play-tab></mobile-play-tab></div>`;
    }

    return html`
      <div class="mobile-layout">
        <game-invitation-banner></game-invitation-banner>
        <friend-request-banner></friend-request-banner>
        ${content}
        <mobile-nav-bar></mobile-nav-bar>
      </div>
    `;
  }

  render() {
    // Error state
    if (this.error) {
      return html`
        <div class="error-container">
          <div class="text-center">
            <h1 class="text-danger mb-3">Failed to load application</h1>
            <p class="text-muted">${this.error}</p>
            <p class="text-muted">Please check the console for more details.</p>
          </div>
        </div>
      `;
    }

    // While bootstrapping auth, render a blank container so we don't flash
    // the title screen before navigating to the destination layout.
    if (this.loading) {
      return html`<div class="app-container"></div>`;
    }

    const meta = router.meta;

    // Public routes render without auth
    if (meta.layout === 'public') {
      return this._renderPublicLayout(meta);
    }

    // Mobile layout branch
    if (platformState.isMobile) {
      // No user — mobile sign-in (always require auth on mobile)
      if (!this.user) {
        return html`
          <div class="app-container">
            <mobile-sign-in @login="${this._handleLogin}"></mobile-sign-in>
          </div>
        `;
      }

      // Staff routes redirect to play on mobile
      if (meta.layout === 'staff' || meta.layout === 'staff-editor') {
        router.navigate('/play');
        return html`<div class="app-container"></div>`;
      }

      return this._renderMobileLayout(meta);
    }

    // Auth required but no user — show title screen with sign-in prompt
    if (meta.auth && !this.user) {
      return html`
        <div class="app-container">
          <title-screen
            @login="${this._handleLogin}"
            .desktopDownloadUrl="${this.desktopDownloadUrl}"
          ></title-screen>
        </div>
      `;
    }

    // Route-driven layout (desktop)
    switch (meta.layout) {
      case 'title':
        return this._renderTitleLayout(meta);
      case 'fullscreen':
        return this._renderFullscreenLayout(meta);
      case 'staff':
        return this._renderStaffLayout(meta);
      case 'staff-editor':
        return this._renderStaffEditorLayout(meta);
      case 'public':
        return this._renderPublicLayout(meta);
      default:
        return this._renderTitleLayout(meta);
    }
  }
}

customElements.define('rpgish-app', RpgishApp);
